import { AfterViewInit, Component, ElementRef, HostBinding, NgZone, OnChanges, OnDestroy, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { Subscription } from 'rxjs';

import { ViewRenderer } from '@unpispas/upp-wdgt';

import { languageService } from '@unpispas/upp-base';
import { viewService } from '@unpispas/upp-base';

import { Product } from '@unpispas/upp-data';
import { CatalogGrouped } from '@unpispas/upp-data';
import { i18nSupport } from '@unpispas/upp-data';

import { PlaceView } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';
import { PreselectView } from '@unpispas/upp-data';
import { ProductView } from '@unpispas/upp-data';

/********************************/
/* Presentation: catalog sections */
/********************************/

type CatalogEntry =
    | { kind: 'subtitle'; group: ProductView }
    | { kind: 'product'; product: ProductView }
    | { kind: 'preselect'; select: PreselectView };

type CatalogSection = {
    group: ProductView | null;
    entries: CatalogEntry[];
};

/**
 * Flattens {@link PlaceView.GroupedProducts} into first-level sections with nested
 * subgroups as subtitles. Presentation mapping only; does not reorder the model.
 */
class CatalogSections {
    private _source: CatalogGrouped | null = null;
    private _byParent: Map<ProductView | null, ProductView[]> | null = null;
    private _sections: CatalogSection[] | null = null;

    Invalidate(): void {
        this._source = null;
        this._byParent = null;
        this._sections = null;
    }

    Sections(place: PlaceView | null): CatalogSection[] {
        if (!place) {
            return [];
        }

        const grouped = place.GroupedProducts;
        if (this._sections !== null && this._source === grouped) {
            return this._sections;
        }

        this._source = grouped;
        this._byParent = new Map();
        for (const entry of grouped) {
            this._byParent.set(entry.parent, entry.children);
        }

        const root = this._byParent.get(null) ?? [];
        const sections: CatalogSection[] = [];
        const loose: CatalogEntry[] = [];

        for (const child of root) {
            if (child.IsGroup) {
                if (!this._IsVisibleGroup(child)) {
                    continue;
                }

                const entries = this._FlattenChildren(child);
                if (this._HasProduct(entries)) {
                    sections.push({ group: child, entries });
                }
                continue;
            }

            loose.push(...this._EmitLeafEntries(child));
        }

        if (loose.length > 0) {
            sections.push({ group: null, entries: loose });
        }

        this._sections = sections;
        return sections;
    }

    private _ChildrenOf(group: ProductView): ProductView[] {
        return this._byParent?.get(group) ?? [];
    }

    /** Base product row when the leaf is not a family/group and not HideApp. */
    private _IsVisibleProduct(product: ProductView): boolean {
        return !product.IsFamily && !product.HideApp && !product.IsGroup;
    }

    private _AvailableSelects(product: ProductView): PreselectView[] {
        if (product.IsFamily || product.IsGroup) {
            return [];
        }

        return (product.selects ?? []).filter((select) => select.IsAvailable);
    }

    /**
     * Emits the base product row (when visible) then available preselects in model order.
     * HideApp parents contribute only available preselect rows.
     */
    private _EmitLeafEntries(product: ProductView): CatalogEntry[] {
        if (product.IsFamily || product.IsGroup) {
            return [];
        }

        const entries: CatalogEntry[] = [];

        if (this._IsVisibleProduct(product)) {
            entries.push({ kind: 'product', product });
        }

        for (const select of this._AvailableSelects(product)) {
            entries.push({ kind: 'preselect', select });
        }

        return entries;
    }

    private _IsVisibleGroup(group: ProductView): boolean {
        if (group.IsFamily || group.HideApp) {
            return false;
        }

        return this._HasVisibleLeaf(group);
    }

    private _HasVisibleLeaf(group: ProductView): boolean {
        for (const child of this._ChildrenOf(group)) {
            if (child.IsFamily) {
                continue;
            }

            if (child.IsGroup) {
                if (child.HideApp) {
                    continue;
                }

                if (this._HasVisibleLeaf(child)) {
                    return true;
                }
                continue;
            }

            if (this._IsVisibleProduct(child) || this._AvailableSelects(child).length > 0) {
                return true;
            }
        }

        return false;
    }

    private _FlattenChildren(group: ProductView): CatalogEntry[] {
        const entries: CatalogEntry[] = [];

        for (const child of this._ChildrenOf(group)) {
            if (child.IsFamily) {
                continue;
            }

            if (child.IsGroup) {
                if (child.HideApp || !this._HasVisibleLeaf(child)) {
                    continue;
                }

                entries.push({ kind: 'subtitle', group: child });
                entries.push(...this._FlattenChildren(child));
                continue;
            }

            entries.push(...this._EmitLeafEntries(child));
        }

        return entries;
    }

    private _HasProduct(entries: CatalogEntry[]): boolean {
        return entries.some((entry) => entry.kind === 'product' || entry.kind === 'preselect');
    }
}

/********************************/
/* Scroll-spy decision + jump lock */
/********************************/

/**
 * Pure section index resolution and programmatic-jump lock for the chips bar.
 * Measurement stays in the host; this class only decides and gates updates.
 */
class SectionSpy {
    static readonly MARGIN = 12;
    static readonly IDLE_MS = 120;

    private _locked = false;
    private _idleTimer: ReturnType<typeof setTimeout> | null = null;

    get IsLocked(): boolean {
        return this._locked;
    }

    /**
     * Last section whose top edge is at or above the spy margin; at max scroll, the last section.
     */
    static Resolve(
        tops: number[],
        scrollTop: number,
        viewport: number,
        scrollHeight: number,
        margin: number = SectionSpy.MARGIN,
    ): number {
        if (tops.length === 0) {
            return 0;
        }

        const maxScroll = Math.max(0, scrollHeight - viewport);
        if (maxScroll > 0 && scrollTop >= maxScroll - 0.5) {
            return tops.length - 1;
        }

        let active = 0;
        for (let i = 0; i < tops.length; i++) {
            if (tops[i] <= margin) {
                active = i;
            }
        }

        return active;
    }

    /**
     * Horizontal scrollLeft that centres `chipLeft`/`chipWidth` in a bar of `barWidth`.
     */
    static CenterScrollLeft(
        chipLeft: number,
        chipWidth: number,
        barWidth: number,
        scrollWidth: number,
    ): number {
        const max = Math.max(0, scrollWidth - barWidth);
        const target = chipLeft - (barWidth - chipWidth) / 2;
        if (target < 0) {
            return 0;
        }
        if (target > max) {
            return max;
        }
        return target;
    }

    Lock(): void {
        this._ClearIdle();
        this._locked = true;
    }

    Unlock(): void {
        this._ClearIdle();
        this._locked = false;
    }

    /** Schedule unlock after idle (fallback when `scrollend` is missing). */
    ArmIdleUnlock(onUnlock: () => void): void {
        this._ClearIdle();
        this._idleTimer = setTimeout(() => {
            this._idleTimer = null;
            this._locked = false;
            onUnlock();
        }, SectionSpy.IDLE_MS);
    }

    NoteScrollWhileLocked(onUnlock: () => void): void {
        if (!this._locked) {
            return;
        }

        this.ArmIdleUnlock(onUnlock);
    }

    private _ClearIdle(): void {
        if (this._idleTimer) {
            clearTimeout(this._idleTimer);
            this._idleTimer = null;
        }
    }

    Destroy(): void {
        this.Unlock();
    }
}

/** Guest catalog list with group chips and scroll-spy. */
@Component({
    selector: 'upp-guest-catalog',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-catalog.html',
    styleUrls: ['./upp-catalog.scss'],
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef],
    }],
})
export class UppGuestCatalogComponent extends i18nSupport implements AfterViewInit, OnChanges, OnDestroy {
    @Input() place: PlaceView | null = null;
    @Input() ticket: TicketView | null = null;
    /** Search hits when active; `null` means section mode. */
    @Input() results: Array<ProductView | PreselectView> | null = null;

    @Output() Select = new EventEmitter<ProductView | PreselectView>();

    @HostBinding('class.mobile') get HostMobile(): boolean {
        return this.view.Mobile;
    }

    @HostBinding('class.desktop') get HostDesktop(): boolean {
        return this.view.Desktop;
    }

    @ViewChild('listScroll', { read: ElementRef }) private _listScrollHost: ElementRef<HTMLElement> | null = null;
    @ViewChild('chipsScroll', { read: ElementRef }) private _chipsScrollHost: ElementRef<HTMLElement> | null = null;
    @ViewChildren('sectionEl', { read: ElementRef }) private _sectionEls: QueryList<ElementRef<HTMLElement>> | null = null;
    @ViewChildren('chipEl', { read: ElementRef }) private _chipEls: QueryList<ElementRef<HTMLElement>> | null = null;

    private _place_subscription: Subscription | null = null;
    private _sections_subscription: Subscription | null = null;
    private readonly _sections = new CatalogSections();
    private readonly _spy = new SectionSpy();

    private _listContent: HTMLElement | null = null;
    private _raf: number | null = null;
    private _activeIndex = 0;
    private _scrollBound: (() => void) | null = null;
    private _scrollEndBound: (() => void) | null = null;
    private _userBreakBound: (() => void) | null = null;
    private _viewReady = false;

    constructor(
        private lang: languageService,
        private change: ViewRenderer,
        public view: viewService,
        private zone: NgZone,
    ) {
        super(lang, change.cdref);
    }

    ngAfterViewInit(): void {
        this._viewReady = true;
        this._BindListScroll();
        if (this._sectionEls) {
            this._sections_subscription = this._sectionEls.changes.subscribe(() => {
                this._ResetSpyState();
                this._BindListScroll();
            });
        }
    }

    ngOnChanges(): void {
        if (this._place_subscription) {
            this._place_subscription.unsubscribe();
            this._place_subscription = null;
        }

        this._sections.Invalidate();
        this._ResetSpyState();

        if (this.place) {
            this._place_subscription = this.place.OnViewRefresh.subscribe(
            () => {
                this._sections.Invalidate();
                this._ResetSpyState();
                this.change.markForCheck();
                if (this._viewReady) {
                    queueMicrotask(() => this._BindListScroll());
                }
            });
        }

        this.change.markForCheck();
        if (this._viewReady) {
            queueMicrotask(() => this._BindListScroll());
        }
    }

    ngOnDestroy(): void {
        this._UnbindListScroll();
        this._spy.Destroy();

        if (this._sections_subscription) {
            this._sections_subscription.unsubscribe();
            this._sections_subscription = null;
        }

        if (this._place_subscription) {
            this._place_subscription.unsubscribe();
            this._place_subscription = null;
        }

        super.OnDestroy();
    }

    /********************************/
    /* Sections / search            */
    /********************************/

    get Searching(): boolean {
        return this.results !== null;
    }

    get sections(): CatalogSection[] {
        if (this.Searching) {
            return [];
        }

        return this._sections.Sections(this.place);
    }

    get searchRows(): Array<ProductView | PreselectView> {
        if (!this.results) {
            return [];
        }

        return this.results.filter((item) => {
            if (item.ViewOf(Product)) {
                const product = item as ProductView;
                return !product.IsFamily && !product.HideApp && !product.IsGroup;
            }

            const select = item as PreselectView;
            return select.IsAvailable;
        });
    }

    get IsEmpty(): boolean {
        if (this.Searching) {
            return this.searchRows.length === 0;
        }

        return this.sections.length === 0;
    }

    get ShowChips(): boolean {
        return !this.Searching && this.sections.length >= 2;
    }

    get ActiveSectionIndex(): number {
        return this._activeIndex;
    }

    /********************************/
    /* TrackBy                      */
    /********************************/

    SectionTrack(_index: number, section: CatalogSection): string | null {
        return section.group?._uuid ?? '__other__';
    }

    EntryTrack(_index: number, entry: CatalogEntry): string | null {
        if (entry.kind === 'subtitle') {
            return `sub-${entry.group._uuid}`;
        }

        if (entry.kind === 'preselect') {
            return entry.select._uuid;
        }

        return entry.product._uuid;
    }

    ProductTrack(_index: number, item: ProductView | PreselectView): string | null {
        return item?._uuid || null;
    }

    /********************************/
    /* Handlers                     */
    /********************************/

    OnSelect(item: ProductView | PreselectView): void {
        this.Select.emit(item);
    }

    OnChipSelect(index: number): void {
        if (!this.ShowChips || index < 0 || index >= this.sections.length) {
            return;
        }

        const content = this._ListContent();
        const sectionEl = this._sectionEls?.get(index)?.nativeElement;
        if (!content || !sectionEl) {
            return;
        }

        this._SetActiveIndex(index);
        this._spy.Lock();
        this._CenterActiveChip();

        const maxTop = Math.max(0, content.scrollHeight - content.clientHeight);
        const target = Math.min(Math.max(0, sectionEl.offsetTop), maxTop);
        const behavior = this._PrefersReducedMotion() ? 'auto' : 'smooth';

        content.scrollTo({ top: target, behavior });
        this._spy.ArmIdleUnlock(() => undefined);
    }

    /********************************/
    /* Scroll / spy (private)       */
    /********************************/

    private _ResetSpyState(): void {
        this._spy.Unlock();
        this._activeIndex = 0;
        this._CancelRaf();
    }

    private _ListContent(): HTMLElement | null {
        const host = this._listScrollHost?.nativeElement;
        if (!host) {
            return null;
        }

        return host.querySelector('.upp-scrollable-content') as HTMLElement | null;
    }

    private _ChipsContent(): HTMLElement | null {
        const host = this._chipsScrollHost?.nativeElement;
        if (!host) {
            return null;
        }

        return host.querySelector('.upp-scrollable-content') as HTMLElement | null;
    }

    private _BindListScroll(): void {
        this._UnbindListScroll();

        if (!this.ShowChips) {
            return;
        }

        const content = this._ListContent();
        if (!content) {
            return;
        }

        this._listContent = content;

        this.zone.runOutsideAngular(() => {
            this._scrollBound = () => this._OnListScroll();
            this._scrollEndBound = () => this._OnScrollEnd();
            this._userBreakBound = () => this._OnUserBreak();

            content.addEventListener('scroll', this._scrollBound, { passive: true });
            content.addEventListener('scrollend', this._scrollEndBound as EventListener);
            content.addEventListener('wheel', this._userBreakBound, { passive: true });
            content.addEventListener('touchstart', this._userBreakBound, { passive: true });
            content.addEventListener('pointerdown', this._userBreakBound, { passive: true });
        });

        this._ScheduleSpy();
    }

    private _UnbindListScroll(): void {
        const content = this._listContent;
        if (content && this._scrollBound) {
            content.removeEventListener('scroll', this._scrollBound);
        }
        if (content && this._scrollEndBound) {
            content.removeEventListener('scrollend', this._scrollEndBound as EventListener);
        }
        if (content && this._userBreakBound) {
            content.removeEventListener('wheel', this._userBreakBound);
            content.removeEventListener('touchstart', this._userBreakBound);
            content.removeEventListener('pointerdown', this._userBreakBound);
        }

        this._listContent = null;
        this._scrollBound = null;
        this._scrollEndBound = null;
        this._userBreakBound = null;
        this._CancelRaf();
    }

    private _OnListScroll(): void {
        if (this._spy.IsLocked) {
            this._spy.NoteScrollWhileLocked(() => undefined);
            return;
        }

        this._ScheduleSpy();
    }

    private _OnScrollEnd(): void {
        if (this._spy.IsLocked) {
            this._spy.Unlock();
            this._ScheduleSpy();
        }
    }

    private _OnUserBreak(): void {
        if (this._spy.IsLocked) {
            this._spy.Unlock();
        }
    }

    private _ScheduleSpy(): void {
        if (this._raf !== null) {
            return;
        }

        this._raf = requestAnimationFrame(() => {
            this._raf = null;
            this._RunSpy();
        });
    }

    private _CancelRaf(): void {
        if (this._raf !== null) {
            cancelAnimationFrame(this._raf);
            this._raf = null;
        }
    }

    private _RunSpy(): void {
        if (this._spy.IsLocked || !this.ShowChips) {
            return;
        }

        const content = this._ListContent();
        const sectionEls = this._sectionEls;
        if (!content || !sectionEls || sectionEls.length === 0) {
            return;
        }

        const containerRect = content.getBoundingClientRect();
        const tops: number[] = [];
        sectionEls.forEach((ref) => {
            tops.push(ref.nativeElement.getBoundingClientRect().top - containerRect.top);
        });

        const next = SectionSpy.Resolve(
            tops,
            content.scrollTop,
            content.clientHeight,
            content.scrollHeight,
        );

        if (next !== this._activeIndex) {
            this.zone.run(() => {
                this._SetActiveIndex(next);
                this._CenterActiveChip();
            });
        }
    }

    private _SetActiveIndex(index: number): void {
        this._activeIndex = index;
        this.change.markForCheck();
    }

    private _CenterActiveChip(): void {
        const bar = this._ChipsContent();
        const chip = this._chipEls?.get(this._activeIndex)?.nativeElement;
        if (!bar || !chip) {
            return;
        }

        bar.scrollLeft = SectionSpy.CenterScrollLeft(
            chip.offsetLeft,
            chip.offsetWidth,
            bar.clientWidth,
            bar.scrollWidth,
        );
    }

    private _PrefersReducedMotion(): boolean {
        if (typeof matchMedia !== 'function') {
            return false;
        }

        return matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
}
